import React, { useState } from "react";
import { Modal, Tooltip, Tag, Button } from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import api from "../../../../services/api";
import { showBasicAlert } from "../../../../core/common/useAlert";
import styles from "./ViewTemplatesModal.module.css";
import EditCampaignModal from "./EditCampaignModal";

interface Props {
  open: boolean;
  onClose: () => void;
  campaigns: any[];
  refresh: () => void;
}

const ViewTemplatesModal: React.FC<Props> = ({
  open,
  onClose,
  campaigns,
  refresh,
}) => {
  const signature = localStorage.getItem("signature");

  const [visibleApiKeyIndex, setVisibleApiKeyIndex] = useState<number | null>(
    null
  );
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editedCampaigns, setEditedCampaigns] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditCampaign, setCurrentEditCampaign] = useState<any>(null);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);

  const handleToggleStatus = async (id: string, status: number) => {
    try {
      await api.post("whatsappService/toggleTemplateStatus", {
        signature,
        id,
        status: status === 1 ? 0 : 1,
      });
      refresh();
    } catch {
      showBasicAlert("Failed to toggle status");
    }
  };

  const handleEdit = (index: number) => {
    setCurrentEditIndex(index);
    setCurrentEditCampaign({ ...campaigns[index] });
    setEditModalOpen(true);
  };

  const handleEditModalChange = (field: string, value: string) => {
    setCurrentEditCampaign((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleEditModalSave = async () => {
    try {
      await api.post("whatsappService/updateTemplate", {
        signature,
        id: currentEditCampaign.id,
        campaign_name: currentEditCampaign.campaign_name,
        leadMessage: currentEditCampaign.leadMessage,
      });
      showBasicAlert("Template updated successfully");
      setEditModalOpen(false);
      refresh();
    } catch {
      showBasicAlert("Failed to update template");
    }
  };

  const handleUpdate = async (index: number) => {
    const campaign = editedCampaigns[index];
    var data = {
      signature,
      id: campaign.id,
      campaign_name: campaign.campaign_name,
    };
    try {
      await api.post("whatsappService/updateTemplate", data);
      showBasicAlert("Template updated successfully");
      setEditIndex(null);
      refresh();
    } catch {
      showBasicAlert("Failed to update template");
    }
  };

  const handleChange = (index: number, field: string, value: string) => {
    const newData = [...editedCampaigns];
    newData[index][field] = value;
    setEditedCampaigns(newData);
  };

  return (
    <Modal
      title="Registered Campaign Templates"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ maxWidth: "90vw" }}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.templateTable}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Campaign</th>
              <th>Type</th>
              <th>Message</th>
              <th>Method</th>
              <th>URL</th>
              <th>API Key</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((item, index) => (
              <tr key={index}>
                <td data-label="#"> {index + 1} </td>

                {/* Campaign name */}
                <td data-label="Campaign">
                  {editIndex === index ? (
                    <input
                      value={editedCampaigns[index]?.campaign_name}
                      onChange={(e) =>
                        handleChange(index, "campaign_name", e.target.value)
                      }
                      className={styles.input}
                    />
                  ) : (
                    item.campaign_name
                  )}
                </td>

                {/* Template type */}
                <td data-label="Type">
                  <Tag color={"blue"}>
                    {item.template_type.replaceAll("-", " ").toUpperCase()}
                  </Tag>
                </td>

                <td data-label="Message">
                  {editIndex === index ? (
                    <input
                      value={editedCampaigns[index]?.leadMessage}
                      onChange={(e) =>
                        handleChange(index, "leadMessage", e.target.value)
                      }
                      className={styles.input}
                    />
                  ) : (
                    <div className={styles.ellipsis} title={item.leadMessage}>
                      {item.leadMessage}
                    </div>
                  )}
                </td>

                {/* Method */}
                <td data-label="Method">
                  <Tag color={item.api_method === "POST" ? "orange" : "purple"}>
                    {item.api_method}
                  </Tag>
                </td>

                {/* URL with ellipsis + tooltip */}
                <td data-label="URL" className={styles.ellipsisCell}>
                  <Tooltip title={item.url}>{item.url}</Tooltip>
                </td>

                {/* API key – scrolls by itself */}
                <td data-label="API Key">
                  <div className={styles.apiKeyWrapper}>
                    {visibleApiKeyIndex === index ? item.api_key : "••••••••"}
                  </div>
                  <Button
                    type="link"
                    size="small"
                    icon={
                      visibleApiKeyIndex === index ? (
                        <EyeInvisibleOutlined />
                      ) : (
                        <EyeOutlined />
                      )
                    }
                    onClick={() =>
                      setVisibleApiKeyIndex(
                        visibleApiKeyIndex === index ? null : index
                      )
                    }
                  />
                </td>

                {/* Status toggle */}
                <td data-label="Status">
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={item.status === 1}
                      onChange={() => handleToggleStatus(item.id, item.status)}
                    />
                    <span className={styles.slider} />
                  </label>
                </td>

                {/* Edit / Update */}
                <td data-label="Actions">
                  {editIndex === index ? (
                    <Button
                      icon={<SaveOutlined />}
                      type="primary"
                      size="small"
                      onClick={() => handleUpdate(index)}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => handleEdit(index)}
                    >
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {currentEditCampaign && (
          <EditCampaignModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            campaign={currentEditCampaign}
            onChange={handleEditModalChange}
            onSave={handleEditModalSave}
          />
        )}
      </div>
    </Modal>
  );
};

export default ViewTemplatesModal;
